import React from "react";
import { Hooks, Platform } from "@xt/xtbase";
import qrImg from "@/assets/images/homePc/spikex-qr.png";
import phoneImg from "@/assets/images/homePc/iPhone-13.png";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

function AndroidIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M8.7 2.4 10.5 5.1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none" />
      <path d="M15.3 2.4 13.5 5.1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" fill="none" />
      <path
        fillRule="evenodd"
        d="M6.8 9 A5.2 5.2 0 0 1 17.2 9 Z M10.75 6.3 A0.75 0.75 0 1 1 9.25 6.3 A0.75 0.75 0 1 1 10.75 6.3 Z M14.75 6.3 A0.75 0.75 0 1 1 13.25 6.3 A0.75 0.75 0 1 1 14.75 6.3 Z"
      />
      <rect x="6.8" y="9.6" width="10.4" height="8" rx="1.5" />
      <rect x="3.5" y="9.9" width="2.2" height="6.2" rx="1.1" />
      <rect x="18.3" y="9.9" width="2.2" height="6.2" rx="1.1" />
      <rect x="8.5" y="16.9" width="2.3" height="5.5" rx="1.15" />
      <rect x="13.2" y="16.9" width="2.3" height="5.5" rx="1.15" />
    </svg>
  );
}

function AppleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function GooglePlayIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  );
}

const DOWNLOAD_URLS = {
  ios: "https://apps.apple.com/app/id6758935015",
  "android-google-play": "https://play.google.com/store/apps/details?id=com.spikex.app.droid",
  android: "https://a.spikex.com/app/Spikex.apk",
};

const PLATFORMS = [
  { key: "android", label: "Android", Icon: AndroidIcon },
  { key: "ios", label: "App Store", Icon: AppleIcon },
  { key: "android-google-play", label: "Google Play", Icon: GooglePlayIcon },
];

export default function DownloadSection() {
  const t = useTranslation();

  const onDownLoad = (type) => {
    if (Platform.isWeiXin() && ["android", "android-google-play"].includes(type)) {
      return;
    }
    const url = DOWNLOAD_URLS[type];
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <section className={styles.section}>
      <div className={`${styles.card} ${styles.downloadCard}`}>
        <div className={styles.phoneMock}>
          <div className={styles.phoneGlow} aria-hidden />
          <img
            src={phoneImg}
            alt=""
            className={styles.phoneImg}
            width={300}
            height={513}
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className={styles.downloadCopy}>
          <h2 className={styles.downloadTitle}>
            {t("home.pcDownloadTitle1") || "Trade on the go."}
            <br />
            {t("home.pcDownloadTitle2") || "Anywhere, anytime."}
          </h2>

          <div className={styles.qrRow}>
            <div className={styles.qrWrap}>
              <div className={styles.qrGlow} aria-hidden />
              <div className={styles.qrBox}>
                <img
                  src={qrImg}
                  alt={t("home.pcScanDownload") || "Scan to Download App"}
                  className={styles.qrImg}
                  width={180}
                  height={180}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <div>
              <div className={styles.qrHint}>{t("home.pcScanDownload") || "Scan to Download App"}</div>
              <div className={styles.qrPlatforms}>{t("home.pcIosAndroid") || "iOS and Android"}</div>
            </div>
          </div>

          <div className={styles.platformRow}>
            {PLATFORMS.map(({ key, label, Icon }) => (
              <button key={key} type="button" className={styles.platformBtn} onClick={() => onDownLoad(key)}>
                <Icon className={styles.platformIcon} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
